<template>
  <v-row>
    <v-col v-if="loading" cols="12">
      <v-skeleton-loader type="image" />
    </v-col>
    <v-col v-else class="py-0" cols="12">
      <chart-card
        :title="formatTitle"
        :subtitle="formatSubtitle"
        display-mode
        :data="chartData"
        loader="charts-comparativo"
        :controls="false"
        legends
        :chartHeight="180"
        chartClass="elevation-0"
        :is-integer="isIndicatorInteger"
        :configUpdate="{
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          hover: { mode: true },
        }"
      />
    </v-col>
  </v-row>
</template>

<script>
import ChartCard from '@/components/ChartCard.vue'
import { colors } from '@/config/colors.js'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { QUERY_GET_METRICS_GRAPHIC } from '../../graphql'

export default {
  name: 'DecompositionDetailsGraphic',
  components: {
    ChartCard,
  },
  props: {
    detailIndicator: {
      type: String,
      required: true,
    },
    graphicTitle: {
      type: String,
      default: '',
    },
    units: {
      type: Array,
      required: true,
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    segments: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    loading: false,
    selectedDetail: '',
    dataGraphic: {},
  }),
  computed: {
    formatTitle() {
      return `Gráfico de ${this.graphicTitle.toLowerCase()}`
    },
    formatSubtitle() {
      if (!isEmpty(this.formatChartData)) {
        const length = this.formatChartData[this.indicatorItens[this.detailIndicator][0]].length
        const { year: initialYear, completeMonth: initialMonth } = this.formatChartData[this.indicatorItens[this.detailIndicator][0]][0]
        const { year: endYear, completeMonth: endMonth } = this.formatChartData[this.indicatorItens[this.detailIndicator][0]][length - 1]

        const formatDates = `${initialMonth} de ${initialYear} a ${endMonth} de ${endYear}`

        return `Nos últimos 12 meses | ${formatDates}`
      }

      return ''
    },
    formatChartData() {
      const chartArray = Object.keys(this.dataGraphic).reduce((accumulator, currentData) => {
        accumulator[currentData] = this.dataGraphic[currentData].map((item) => {
          const { date, value } = item
          const month = DateTime.fromISO(date).toFormat('MMM')
          const year = DateTime.fromISO(date).toFormat('yyyy')
          const monthYear = DateTime.fromISO(date).toFormat('MMM/yyyy')
          let completeMonth = DateTime.fromISO(date).toFormat('MMMM')
          completeMonth = completeMonth.charAt(0).toUpperCase() + completeMonth.slice(1)

          return { month, monthYear, completeMonth, year, value }
        })
        return accumulator
      }, {})

      return isEmpty(this.dataGraphic) ? {} : chartArray
    },
    indicatorItens() {
      return {
        AVERAGE_ITEMS_IN_BASKET: ['loyalty_average_items_in_basket', 'non_loyalty_average_items_in_basket'],
        AVERAGE_PRODUCT_VALUE: ['loyalty_average_product_value', 'non_loyalty_average_product_value'],
        UNIQUE_CUSTOMERS: ['unique_customers', 'estimated_total_number_of_customers'],
        AVERAGE_CUSTOMER_SPEND: ['average_customer_coupons'],
      }
    },
    isIndicatorInteger() {
      const isIndicatorInteger = {
        AVERAGE_ITEMS_IN_BASKET: false,
        AVERAGE_PRODUCT_VALUE: false,
        UNIQUE_CUSTOMERS: true,
        AVERAGE_CUSTOMER_SPEND: false,
      }
      return isIndicatorInteger[this.detailIndicator]
    },
    chartDataLabels() {
      return {
        loyalty_average_items_in_basket: 'Média de itens na cesta - Clube',
        non_loyalty_average_items_in_basket: 'Média de itens na cesta - Fora do clube',
        loyalty_average_product_value: 'Valor médio dos produtos - Clube',
        non_loyalty_average_product_value: 'Valor médio dos produtos - Fora do clube',
        unique_customers: 'Clientes fidelidade únicos',
        estimated_total_number_of_customers: 'Estimativa de clientes totais',
        average_customer_coupons: 'Média de compras por cliente',
      }
    },
    chartData() {
      let labels = []
      let datasets = [
        {
          backgroundColor: colors[1],
          strokeColor: colors[1],
          borderColor: colors[1],
          data: [],
          fill: false,
        },
      ]
      if (this.indicatorItens[this.detailIndicator].length === 2) {
        datasets.push({
          backgroundColor: colors[0],
          strokeColor: colors[0],
          borderColor: colors[0],
          data: [],
          fill: false,
        })
      }
      let labelLegend = 'month'

      if (this.detailIndicator === 'UNIQUE_CUSTOMERS' || this.detailIndicator === 'AVERAGE_CUSTOMER_SPEND') {
        labelLegend = 'monthYear'
      }

      labels = isEmpty(this.formatChartData) ? [] : this.formatChartData[this.indicatorItens[this.detailIndicator][0]].map((item) => item[labelLegend])
      this.indicatorItens[this.detailIndicator].forEach((indicator, index) => {
        datasets[index].label = this.chartDataLabels[indicator]
        datasets[index].data = isEmpty(this.formatChartData) ? [] : this.formatChartData[indicator].map((item) => item.value)
      })

      return {
        labels,
        datasets,
      }
    },
  },
  watch: {
    detailIndicator() {
      if (this.showDetails && this.detailIndicator !== this.selectedDetail) {
        this.actions = []

        this.getGraphicData()
      }
      this.selectedDetail = this.detailIndicator
    },
  },
  mounted() {
    this.getGraphicData()
  },
  methods: {
    getDates() {
      const today = DateTime.now()
      const currentDate = today.toFormat('yyyy-MM-dd')
      const lastYearDate = today.minus({ year: 1 }).toFormat('yyyy-MM-dd')

      return { currentDate, lastYearDate }
    },
    getGraphicData() {
      this.loading = true
      const { currentDate, lastYearDate } = this.getDates()
      const variables = {
        initialDate: lastYearDate,
        finalDate: currentDate,
        units: this.units,
        segments: this.segments,
        products: this.products,
        groupBy: 'MONTH',
      }

      return this.$apollo
        .query({
          query: QUERY_GET_METRICS_GRAPHIC,
          variables,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['campaigns'],
          },
        })
        .then(({ data: { metricsKpi } }) => {
          const { __typename, ...data } = metricsKpi

          this.dataGraphic = data
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.$snackbar({ message: `Erro ao buscar os dados.` })
        })
    },
  },
}
</script>
